import { Fragment, useRef } from 'react';
import {
  Box,
  Card,
  Stack,
  FormControl,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  NoDataAvailable
} from '../../components/_dashboard/app';
import { CustomPagination, SnackBarAlert } from '../../components/HelperComponents';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { useRegions } from '../hooks/useRegions';
import RegionMoreMenu from './RegionMoreMenu';
import { AddRegionModal, DeviceOrderdButtons } from '../../components/_dashboard/soundbox';



export default function RegionContentCard() {
  const { sessionUserData, zonesList, fetchZonesList, fetchHeadOfficesList, headOfficesList } = useAuth();
  const regionTableRef = useRef(null);
  const {
    page,
    searchText,
    setSearchText,
    regionsListData,
    setPage,
    isSnackBarAlertOpen,
    setIsSnackBarAlertOpen,
    snackBarMetas,
    handleChangePage,
    handleSearch,
    handleRegionMoreMenuClick,
    regionModalOpen,
    setRegionModalOpen,
    selectedRegionForAction,
    onUpdateRegionAction,
    setSelectedRegionForAction
  } = useRegions()

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        <Button
          variant="contained"
          color="whitebg"
          size="small"
          sx={{ mr: 1, minHeight: '4vh', minWidth: 110 }}
          onClick={() => {
            fetchHeadOfficesList();
            fetchZonesList();
            setRegionModalOpen(true);
            setSelectedRegionForAction(null);
          }}
        >
          Add New Region
        </Button>
      </Box>

      <Card>
        <Stack
          direction={{ md: 'row', xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: 2 }}
          mt={1}
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Search by
              </Typography>
            </FormControl>


            <TextField
              size="small"
              label="Enter Region Name"
              value={searchText}
              onChange={(event) => setSearchText(event?.target?.value)}
              InputProps={{
                inputProps: {
                  'data-testid': 'searchText'
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleSearch(searchText)}
                      aria-label="Search regions"
                    >
                      <SearchOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch(event?.target?.value)
                }
              }}
            />
          </Stack>
        </Stack>

        <Divider sx={{ mx: 1 }} />

        {regionsListData?.data !== null && regionsListData?.data !== undefined ? (
          <>
            <TableContainer
              sx={{ maxHeight: '60vh', minHeight: '60vh', overflowY: 'scroll' }}
              ref={regionTableRef}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 900 }}>Region Name</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Zone Name</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Pincode</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Contact Person</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regionsListData?.loading ? <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow> : null}
                  {regionsListData?.data?.map((row) => (
                    <Fragment key={row?.id}>
                      <TableRow
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        tabIndex={-1}
                      >
                        <TableCell className="merchantTableListLabel" sx={{ textTransform: 'capitalize' }}>
                          {row?.name || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel" sx={{ textTransform: 'capitalize' }}>
                          {zonesList?.find((item) => item.id === row?.zoneOfficeId)?.name || ''}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.city || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.state || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.pincode || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          <Typography className='merchantTableListLabel'>{row?.contactPerson || '---'}</Typography>
                          <Typography className='merchantTableListLabel'>{row?.phone}</Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={row?.status}
                            sx={{
                              fontWeight: 600
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.address || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          <RegionMoreMenu
                            handleUser={row}
                            handleClicks={(action) => handleRegionMoreMenuClick(action, row)}
                          />
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                  {regionsListData?.data?.length === 0 && regionsListData?.data !== true && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <NoDataAvailable />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              handleNextPage={() => handleChangePage('NEXT')}
              handlePreviousPage={handleChangePage}
              hasNextPage={regionsListData?.metas?.hasNextPage}
              data={regionsListData}
              setPage={setPage}
            />
          </>
        ) : null}
      </Card>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />

      <AddRegionModal
        opneAssignedModal={regionModalOpen}
        handleClose={() => {
          setRegionModalOpen(false)
        }}
        headOfficesList={headOfficesList}
        zonesList={zonesList}
        fetchZones={fetchZonesList}
        selectedRegion={selectedRegionForAction}
        onUpdate={(action, msg) => onUpdateRegionAction(action, msg)}
      />
    </>
  );
}
